import { useRef, useCallback, useEffect } from "react";

export const createDelayedActionScheduler = () => {
  let timer: ReturnType<typeof setTimeout> | null = null;

  const cancel = () => {
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
  };

  return {
    schedule: <T>(action: (item: T) => void, item: T, delay: number) => {
      cancel();
      timer = setTimeout(() => {
        timer = null;
        action(item);
      }, delay);
    },
    cancel,
  };
};

/**
 * useDelayedAction
 *
 * Schedules an action after a dwell delay and allows pending work to be
 * canceled when the user's intent ends.
 *
 * @param action - The function to execute after the delay
 * @param delay - Delay in milliseconds (default: 250)
 * @returns Stable schedule and cancel callbacks
 */
const useDelayedAction = <T>(
  action: (item: T) => void,
  delay: number = 250,
) => {
  const schedulerRef =
    useRef<ReturnType<typeof createDelayedActionScheduler>>(undefined);
  const scheduler =
    schedulerRef.current ??
    (schedulerRef.current = createDelayedActionScheduler());

  const schedule = useCallback(
    (item: T) => {
      scheduler.schedule(action, item, delay);
    },
    [action, delay, scheduler],
  );

  const cancel = useCallback(() => {
    scheduler.cancel();
  }, [scheduler]);

  // Also clear a pending action when its callback identity changes. Workbook
  // tabs use that identity to prevent an old period's dwell timer from firing
  // after the viewer has switched to a different workbook.
  useEffect(() => () => scheduler.cancel(), [action, delay, scheduler]);

  return { schedule, cancel };
};

export default useDelayedAction;
